import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Box, Flex } from "../../../../styled-system/jsx";
import { Button } from "../../core/basic/Button";
import Loading from "../../core/blocks/Loading";
import { Table } from "../../table/Table";
import { createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { useDSPClientPreview, useImportDSPClients } from "../../../modules/dsp/hooks/useDSPClientImport";
import { type DSPClientImportRow } from "../../../zodios/apiDefinitions/api/dspClients";
import { successToast, errorToast } from "../../../utils/toasts";

const ELIGIBLE = "eligible";

const columnHelper = createAppColumnHelper<DSPClientImportRow>();

export function ImportDSPClientsDialogue({
  isOpen,
  setShowModal,
}: {
  isOpen: boolean;
  setShowModal: (isOpen: boolean) => void;
}) {
  const { t } = useTranslation(["onboarding", "common"]);

  const { rows, isLoading } = useDSPClientPreview(isOpen);
  const importClients = useImportDSPClients();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(($) => $.onboarding["Profile Name"]),
        minSize: 200,
        size: 300,
      }),
      columnHelper.accessor("entity_id", {
        header: t(($) => $.onboarding["Seller ID"]),
        size: 200,
      }),
      columnHelper.accessor("advertiser_count", {
        header: t(($) => $.onboarding["DSP Advertisers"]),
        size: 150,
        meta: { justify: "end" },
      }),
      columnHelper.accessor(
        (row) => (row.status === ELIGIBLE ? t(($) => $.onboarding["Ready to import"]) : row.detail),
        {
          id: "status",
          header: t(($) => $.onboarding.Status),
          size: 300,
        },
      ),
    ],
    [t],
  );

  const table = useClientTable({
    data: rows,
    columns,
    getRowId: (row) => row.profile_id,
    // Ineligible rows stay visible with their reason, but can't be submitted.
    enableRowSelection: (row) => row.original.status === ELIGIBLE,
    meta: {
      getRowSelectionDisabledReason: (row) => row.original.detail,
    },
  });

  const selectedProfileIds = table.getSelectedRowModel().rows.map((row) => row.original.profile_id);

  return (
    <Box minHeight="200px" marginX="auto" width={{ base: "100%", xl: "80%" }}>
      <p className={css({ textAlign: "center", fontSize: "large", paddingX: "xxlarge" })}>
        {t(
          ($) =>
            $.onboarding[
              "Select the agency profiles to import as DSP clients. Once imported, their advertisers can be assigned to an organization from the DSP Profiles section of the Applications page."
            ],
        )}
      </p>

      {isLoading ?
        <Loading fullPage={false} height={"250px"} />
      : rows.length ?
        <>
          <Table table={table} />

          <Flex justifyContent="center" gap="xlarge" marginTop="xlarge">
            <Button variant="secondary" onPress={() => setShowModal(false)}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button
              onPress={() =>
                importClients.mutate(selectedProfileIds, {
                  onSuccess: (response) => {
                    successToast({
                      message: t(($) => $.onboarding["{count, plural, DSP profiles imported}"], {
                        count: response.data.filter((row) => row.status === "created").length,
                      }),
                    });
                    setShowModal(false);
                  },
                  onError: (error) => {
                    errorToast({ message: t(($) => $.onboarding["Failed to import DSP profiles"]), error });
                  },
                })
              }
              isDisabled={!selectedProfileIds.length || importClients.isPending}
              isPending={importClients.isPending}
            >
              {t(($) => $.onboarding["Import DSP Profiles"])}
            </Button>
          </Flex>
        </>
      : <>
          <p
            className={css({
              textAlign: "center",
              fontSize: "large",
              color: "text.secondary",
              paddingX: "xlarge",
              paddingY: "xlarge",
            })}
          >
            {t(
              ($) =>
                $.onboarding[
                  "No DSP-capable agency profiles were found for this organization. Amazon Attribution profiles are listed as agency profiles but cannot be used for DSP."
                ],
            )}
          </p>
          <Flex justifyContent="center" paddingY="xlarge">
            <Button onPress={() => setShowModal(false)}>{t(($) => $.common.actions.Close)}</Button>
          </Flex>
        </>
      }
    </Box>
  );
}
